import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Grid2 as Grid,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Stack,
    Link,
    Collapse,
    Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import PageFilter from "../../../../Components/Misc/PageFilter";

import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ReplayIcon from "@mui/icons-material/Replay";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import HelpIcon from "@mui/icons-material/Help";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import AttributionIcon from "@mui/icons-material/Attribution";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ActivityDetails from "../../../../Components/Activity/AcitivityDetails/activity-details";
import ActivityUpdate from "../../../../Components/Activity/AcitivityDetails/activity-update";
import Note from "../../../../Components/Misc/Note/note";
import { useSelection } from "../../../../Utils/Hooks/useSelection";
import { useMultipleSelection } from "../../../../Utils/Hooks/useMultipleSelection";
import { cacheService, logService, userService } from "../../../../Core/Api/services";
import Activity, { ActivityMappedData } from "../../../../Components/Activity/activity";
import { ActivityType } from "../../../../Core/Api/Types/activity-type";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import { CommentWrapper } from "../../../../Components/Activity/ActivityMainFrame/activity-main-frame";
import CommentForm from "../../../../Components/Misc/CommentForm/comment-form";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { produce } from "immer";
import { MoveInArray, UserHasAcl } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import { UserNote } from "../../../../Core/Api/Types/update-notes-request";
import moment from "moment";
import { useTranslation } from "react-i18next";

function DashboardOverview() {
    const { t } = useTranslation();
    const [activities, setActivities] = useState<ActivityMappedData[]>([]);
    const [notes, setNotes] = useState<UserNote[]>([]);
    const [currentNote, setCurrentNote] = useState<{ title: string; desc: string }>({ title: "", desc: "" });
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isNoteLoaded, setIsNoteLoaded] = useState<boolean>(false);

    const activityTypes: ActivityType[] = [
        ActivityType.AssetReject,
        ActivityType.OrderFailed,
        ActivityType.OrderCompleted,
        ActivityType.Comment,
    ];
    const hasNotes = true;
    const { isSelected, select } = useSelection("all");
    const { select: expandActivity, isSelected: isActivityExpanded } = useMultipleSelection<number>([]);
    const { select: expandActivityComment, isSelected: isActivityExpandedComment } = useMultipleSelection<number>([]);
    const [query, setQuery] = useState("");
    const [onlyMine, setOnlyMine] = useState(false);
    const [onlyResolved, setOnlyResolved] = useState(false);
    const user = cacheService.GetUserInfo();
    const enqueueCustomSnackbar = useCustomSnackbar();

    const _expandIconButton = React.useCallback(
        (id: number) => (
            <IconButton onClick={() => expandActivity(id)}>
                <ExpandMoreRoundedIcon
                    fontSize="small"
                    sx={{ transform: isActivityExpanded(id) ? "none" : "rotate(-90deg)" }}
                />
            </IconButton>
        ),
        [expandActivity, isActivityExpanded]
    );

    const _actionsToResolve = (resourceId: number, activityType: ActivityType) => {
        switch (activityType) {
            case ActivityType.OrderFailed:
                return (
                    <Button startIcon={<ReplayIcon />} size="small">
                        {t("common:retry")}
                    </Button>
                );
            case ActivityType.AssetReject:
                return (
                    <Button startIcon={<AttributionIcon />} size="small">
                        {t("button:goToAssetAndTakeDecision")}
                    </Button>
                );
        }
    };

    const handleCommentSend = (text: string, activity: ActivityMappedData) => {
        logService
            .InsertActivityComment({
                message: text,
                resId: activity.contextResId,
                kind: activity.activityKind,
                activityReplyId: activity.id,
            })
            .then((response) => {
                enqueueCustomSnackbar(t("alert:success.commentHasBeenPosted"), "success");

                let mapped = logService.GetMappedActivity(response.activity!);

                if (response.activity) {
                    setActivities(
                        produce((draft) => {
                            // Put message at begenning of array to be visible
                            draft.find((x) => x.id === activity.id)!.replies!.unshift(mapped);
                        })
                    );
                }
            });
    };

    const handleCommentReplySend = (text: string, activityId: number) => {
        handleCommentSend(text, activities.find((x) => x.id === activityId)!);
    };

    const markAsSolved = (activityId: number) => {
        logService.SolveActivity(activityId).then((response) => {
            enqueueCustomSnackbar(t("alert:success.activityIsNowSolved"), "success");

            setActivities(
                produce((draft) => {
                    draft.find((x) => x.id === activityId)!.solved = {
                        userId: user!.id,
                        date: moment().toDate(),
                        endDate: null,
                        message: null,
                        comment: null,
                        user: user!,
                        userName: user!.name,
                        id: 0,
                        isNew: true,
                    };
                })
            );
        });
    };

    const writeNote = useCallback((title: string, desc: string) => {
        setCurrentNote({ title: title, desc: desc });
    }, []);

    const postNote = () => {
        setNotes(
            produce((draft) => {
                draft.push({ title: currentNote.title, text: currentNote.desc, date: moment().toDate() });
            })
        );
        writeNote("", "");
    };

    const deleteNote = useCallback((index: number) => {
        setNotes(
            produce((draft) => {
                draft.splice(index, 1);
            })
        );
    }, []);

    // Fired when notes changed and save to backend
    useEffect(() => {
        if (!isNoteLoaded) return;

        userService.UpdateMyNotes({ notes: notes }).then((response) => {
            console.debug("notes updated!");
        });
    }, [notes, isNoteLoaded]);

    // First load
    useEffect(() => {
        logService.ListSessionUserActivitiesByTypes({ types: activityTypes, pageNb: 0, pageSize: 50 }).then((response) => {
            // Expend by default where errors
            response
                .filter((x) => x.error)
                .forEach((x) => {
                    expandActivity(x.id);
                });

            setActivities(response);
            setIsLoading(false);
        });

        userService.GetMyNotes().then((response) => {
            if (response.notes) {
                setNotes(response.notes);
            }
            setIsNoteLoaded(true);
        });
    }, []);

    return (
        <Grid
            container
            columnSpacing={8}
            rowSpacing={2}
            mt={0}
            justifyContent="space-between"
            display="flex"
            flex={1}
            position="relative"
            sx={{
                height: "100%",
                overflow: "hidden",
            }}
        >
            <Grid height={"100%"} size={3} display="flex" flexDirection="column">
                <Stack spacing={1.2} position="sticky" top={"16px"}>
                    <PageFilter
                        active={isSelected("all")}
                        onClick={() => select("all")}
                        small
                        variant="primary"
                        title={activities?.length}
                        subtitle={t("common:all")}
                        icon={<NotificationsOutlinedIcon />}
                    />
                    <PageFilter
                        active={isSelected("errors")}
                        onClick={() => select("errors")}
                        small
                        variant="error"
                        title={activities?.filter((x) => x.error && !x.solved).length}
                        subtitle={t("common:errors")}
                        icon={<CancelOutlinedIcon />}
                    />
                    <PageFilter
                        active={isSelected("success")}
                        onClick={() => select("success")}
                        small
                        variant="success"
                        title={activities?.filter((x) => !x.error).length}
                        subtitle={t("common:success")}
                        icon={<CheckCircleOutlineIcon />}
                    />
                    <PageFilter
                        active={isSelected("replies")}
                        onClick={() => select("replies")}
                        small
                        variant="background"
                        title={activities?.filter((x) => x.replies && x.replies.length > 0).length}
                        subtitle={t("page:pageFilter.subTitle.withCommentsAndReplies")}
                        icon={<ErrorOutlineOutlinedIcon />}
                    />
                </Stack>
                <Box mt="auto" position="sticky" bottom={0}>
                    <FormGroup>
                        <FormControlLabel
                            control={<Checkbox value={onlyResolved} onClick={() => setOnlyResolved((prev) => !prev)} />}
                            label={t("field:label.showResolvedIssues")}
                        />
                        <FormControlLabel
                            control={<Checkbox value={onlyMine} onClick={() => setOnlyMine((prev) => !prev)} />}
                            label={t("field:label.showOnlyMyActivities")}
                        />
                    </FormGroup>
                </Box>
            </Grid>
            <Grid sx={{ height: "100%", overflowY: "scroll" }} size={hasNotes ? 6 : 8}>
                <Box sx={(t) => ({ position: "sticky", top: 0, py: 2, zIndex: 2, background: t.palette.background.default })}>
                    <OutlinedInput
                        label={t("common:search")}
                        sx={{ width: "100%" }}
                        value={query}
                        placeholder={t("common:search")}
                        onChange={(e) => setQuery(e.target.value)}
                        endAdornment={
                            <InputAdornment position="end">
                                {query && (
                                    <IconButton aria-label="Clear field" onClick={() => setQuery("")} size="small" sx={{ mr: 1 }}>
                                        <CloseIcon />
                                    </IconButton>
                                )}
                                <SearchIcon />
                            </InputAdornment>
                        }
                    />
                </Box>
                {/*<Stack direction="row" alignItems="center" spacing={1} mb={3} mt={1}>
            <Chip variant="tag" size="large" label="StudioCanal" onDelete={() => false} />
            <Chip variant="tag" size="large" label="StudioCanal" onDelete={() => false} />
            <Chip variant="tag" size="large" label="StudioCanal" onDelete={() => false} />
          </Stack>*/}

                {activities.length > 0 ? (
                    activities
                        .filter(
                            (x) =>
                                ((onlyMine && x.userId === user?.id) || !onlyMine) &&
                                (query === "" ||
                                    x.contextTitle?.includesAllWords(query) ||
                                    x.contextSubtitle?.includesAllWords(query) ||
                                    x.activityDetails?.includesAllWords(query) ||
                                    x.contextLabel?.includesAllWords(query)) &&
                                ((isSelected("errors") && x.error) || !isSelected("errors")) &&
                                ((isSelected("success") && !x.error) || !isSelected("success")) &&
                                ((isSelected("replies") && x.replies && x.replies.length > 0) || !isSelected("replies")) &&
                                (!onlyResolved || (onlyResolved && x.solved !== null))
                        )
                        ?.map((activity) => (
                            <Box sx={{ pb: 3 }} key={`box-act-${activity.id}`}>
                                <Stack spacing={1.5} direction="row">
                                    <Activity
                                        {...activity}
                                        sx={{ mb: 2 }}
                                        key={activity.id}
                                        expendElement={activity.error ? _expandIconButton(activity.id) : undefined}
                                        handleCommentSend={handleCommentReplySend}
                                    />
                                </Stack>
                                <Collapse in={isActivityExpanded(activity.id)}>
                                    <ActivityDetails pt={2} pb={2}>
                                        <Stack spacing={0.5}>
                                            <ActivityUpdate
                                                text={t("page:activityUpdate.text.selectActionToSolveIssue")}
                                                icon={<AttributionIcon />}
                                                label={t("page:activityUpdate.label.actionsToSolve")}
                                                variant="warning"
                                            />
                                        </Stack>
                                        <Stack mt={1.5} spacing={1} alignItems="flex-start" direction="row">
                                            {_actionsToResolve(activity.id, activity.activityType)}
                                            <Button
                                                startIcon={<HelpIcon />}
                                                size="small"
                                                onClick={() => expandActivityComment(activity.id)}
                                                disabled={!UserHasAcl(AclTypes.aclActivityAdd)}
                                            >
                                                {t("button:requestHelp")}
                                            </Button>
                                            <Button
                                                startIcon={<CheckCircleIcon />}
                                                size="small"
                                                color={activity.solved ? "success" : "primary"}
                                                variant="outlined"
                                                onClick={() => markAsSolved(activity.id)}
                                                disabled={activity.solved !== null}
                                            >
                                                {activity.solved ? t("common:solved") : t("button:markAsSolved")}
                                            </Button>
                                        </Stack>
                                        {isActivityExpandedComment(activity.id) && (
                                            <CommentWrapper>
                                                <CommentForm onSend={(text) => handleCommentSend(text, activity)} sx={{ mt: 2 }} />
                                            </CommentWrapper>
                                        )}
                                    </ActivityDetails>
                                </Collapse>
                            </Box>
                        ))
                ) : (
                    <>{isLoading ? <TableSkeleton /> : <Typography>{t("common:noActivities")}</Typography>}</>
                )}
            </Grid>
            <Grid height={"100%"} size={hasNotes ? 3 : 1}>
                <Stack spacing={1.5} position="sticky" top={"16px"}>
                    {notes.map((note, index) => (
                        <Note
                            key={`note${index}`}
                            title={note.title ?? ""}
                            desc={note.text ?? ""}
                            onDelete={() => deleteNote(index)}
                            onCopy={() => writeNote(note.title ?? "", note.text ?? "")}
                        />
                    ))}
                    <Note
                        editable
                        minHeight={168}
                        title={currentNote.title}
                        desc={currentNote.desc}
                        onTextChange={(title, desc) => writeNote(title, desc)}
                        onAdd={() => postNote()}
                    />
                    <Stack direction="row" justifyContent="space-between">
                        <Link underline="hover" color="primary.text">
                            {t("button:viewAllCount", { count: notes.length })}
                        </Link>
                    </Stack>
                </Stack>
            </Grid>
        </Grid>
    );
}

export default DashboardOverview;
